import React from 'react';
import { graphql } from 'gatsby';
import {
  Layout,
  RelatedStoriesBlock,
  PostcardForm,
} from '../components';
import {
  StoryFeaturedImage,
  StoryGallery,
  StoryNarrative,
  StoryPartners,
} from '../components/Story';
import './story.scss';

const Story = (props) => {
  const {
    data: {
      site: {
        siteMetadata,
      },
      story: {
        data: storyData,
      },
      relatedStories,
    },
    location,
  } = props;
  const {
    storyName,
    category,
    eventDate,
    eventLocation,
    featuredImage,
    narrative,
    partnersHeadline,
    partners = [],
    body,
    metaTitle,
    metaDescription,
    openGraphImage,
  } = storyData;
  const seoData = {
    metaTitle,
    metaDescription,
    openGraphImage,
  };
  const hasPartners = Boolean(partners && partners.length > 0 && partners[0].partnerName.text);
  const hasRelatedStories = Boolean(relatedStories && relatedStories.edges && relatedStories.edges.length > 0);
  const hasGallery = Boolean(body && body.length > 0);
  return (
    <Layout location={location} seoData={seoData}>
      <StoryFeaturedImage
        storyName={storyName}
        category={category}
        featuredImage={featuredImage}
        siteMetadata={siteMetadata}
      />
      <StoryNarrative
        storyName={storyName}
        eventDate={eventDate}
        eventLocation={eventLocation}
        narrative={narrative}
      />
      {hasGallery && (
        <StoryGallery
          sliceBody={body}
          siteMetadata={siteMetadata}
        />
      )}
      {hasPartners && (
        <StoryPartners
          partnersHeadline={partnersHeadline}
          partners={partners}
        />
      )}
      {hasRelatedStories && (
        <RelatedStoriesBlock
          headline="Other Stories"
          relatedStories={relatedStories}
          siteMetadata={siteMetadata}
        />
      )}
      <PostcardForm
        title="Get in touch"
        formName="contact-form-story"
      />
    </Layout>
  );
};

export default Story;

export const pageQuery = graphql`
query StoryBySlug($uid: String!, $categoryUid: String!) {
  site {
    siteMetadata {
      storiesSlug,
      categorySlug,
    }
  },
  story: prismicStory(uid: { eq: $uid }) {
    data {
      storyName: page_name {
        html
        text
      }
      category {
        uid,
        document {
          data {
            categoryName: category_name {
              text
            }
          }
        }
      }
      eventDate: date(formatString: "DD MMM YYYY")
      eventLocation: location {
        html
        text
      }
      featuredImage: featured_image {
        alt
        localFile {
          childImageSharp {
            fluid(maxWidth: 1400, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
      narrative {
        html
        text
      }
      partnersHeadline: partner_headline {
        html
        text
      }
      # partners {
      #   partnerName: partner_name {
      #     html
      #     text
      #   }
      #   partnerType: partner_type {
      #     html
      #     text
      #   }
      #   partnerLink: partner_link {
      #     link_type
      #     url
      #     target
      #   }
      # }
      body {
        ... on PrismicStoryBodyLandscapePhoto {
          slice_type
          id
          primary {
            photo: photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1400, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
        ... on PrismicStoryBodyPortraitPhoto {
          slice_type
          id
          primary {
            photo: photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1400, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
        ... on PrismicStoryBodySquarePhoto {
          slice_type
          id
          primary {
            photo: photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1400, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
        ... on PrismicStoryBodyLandscapePair {
          slice_type
          id
          primary {
            leftPhoto: left_photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
            rightPhoto: right_photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
        ... on PrismicStoryBodyPortraitPair {
          slice_type
          id
          primary {
            leftPhoto: left_photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
            rightPhoto: right_photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
        ... on PrismicStoryBodySquarePair {
          slice_type
          id
          primary {
            leftPhoto: left_photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
            rightPhoto: right_photo {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
      }
      metaTitle: meta_title {
        html
        text
      },
      metaDescription: meta_description {
        html
        text
      },
      openGraphImage: open_graph_image {
        alt
        copyright
        url
      }
    }
  }
  relatedStories: allPrismicStory(
    limit: 2,
    sort: { fields: [data___date], order: DESC }
    filter: { data: { category: { uid: { eq: $categoryUid } } }, uid: { ne: $uid } }
  ) {
    edges {
      node {
        slug: uid
        data {
          storyName: page_name {
            html
            text
          }
          narrative {
            html
            text
          }
          featuredImage: featured_image {
            alt
            localFile {
              childImageSharp {
                fluid(maxWidth: 800, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
      }
    }
  }
}
`;
